import React, { useState, useRef, useEffect } from 'react';
import { FiSend, FiMessageSquare, FiExternalLink, FiCpu, FiInfo } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const DashboardAI = () => {
    const [messages, setMessages] = useState([
        { role: 'bot', text: 'Halo! Saya **NgadhuAI**, asisten pusat data Anda. Saya dapat membantu mencari dataset, menganalisis tren, atau memberikan informasi sektoral Kabupaten Ngada. Apa yang ingin Anda ketahui hari ini?' }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const chatEndRef = useRef(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || loading) return;

        const userMsg = input.trim();
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setLoading(true);

        try {
            const res = await api.post('/ai/ask', { message: userMsg });
            setMessages(prev => [...prev, { 
                role: 'bot', 
                text: res.data.data.answer,
                links: res.data.data.links
            }]);
        } catch (e) {
            setMessages(prev => [...prev, { role: 'bot', text: 'Maaf, terjadi kesalahan teknis saat menghubungi pusat data. Silakan coba lagi nanti.' }]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dashboard-ai-container" style={{ height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div className="dashboard-header" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div className="stat-icon" style={{ background: 'linear-gradient(135deg, var(--primary-600), var(--accent-600))', color: '#fff', width: 42, height: 42 }}>
                        <FiCpu size={20} />
                    </div>
                    <div>
                        <h2>NgadhuAI Assistant</h2>
                        <p>Tanya jawab cerdas berbasis dataset Portal Satu Data Ngada</p>
                    </div>
                </div>
            </div>

            <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '16px', border: '1px solid var(--gray-100)' }}>
                {/* Chat Area */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', background: 'var(--gray-50)' }}>
                    {messages.map((m, i) => (
                        <motion.div 
                            key={i} 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}
                        >
                            <div 
                                style={{ 
                                    padding: '1rem 1.25rem', borderRadius: m.role === 'user' ? '20px 20px 4px 20px' : '20px 20px 20px 4px',
                                    background: m.role === 'user' ? 'var(--primary-600)' : '#fff',
                                    color: m.role === 'user' ? '#fff' : 'var(--text-main)',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                                    fontSize: '0.9rem', lineHeight: '1.6',
                                    border: m.role === 'user' ? 'none' : '1px solid var(--gray-100)'
                                }}
                            >
                                {m.text}
                            </div>
                            {m.links && m.links.length > 0 && (
                                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                        <FiInfo size={12} /> REFERENSI DATASET:
                                    </div>
                                    {m.links.map(lnk => (
                                        <Link to={`/dataset/${lnk.id}`} key={lnk.id} 
                                            className="dataset-link-card"
                                            style={{ 
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', 
                                                padding: '0.75rem 1rem', background: '#fff', border: '1px solid var(--gray-200)', 
                                                borderRadius: '10px', fontSize: '0.8rem', color: 'var(--primary-700)', 
                                                textDecoration: 'none', fontWeight: 600, transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <FiExternalLink size={14} />
                                                <span>{lnk.judul}</span>
                                            </div>
                                            <span style={{ fontSize: '0.7rem', background: 'var(--gray-100)', padding: '2px 8px', borderRadius: '4px', color: 'var(--gray-500)' }}>{lnk.kategori}</span>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    ))}
                    {loading && (
                        <div style={{ alignSelf: 'flex-start', background: '#fff', padding: '1rem', borderRadius: '20px 20px 20px 4px', display: 'flex', gap: '0.4rem', border: '1px solid var(--gray-100)' }}>
                            <div className="dot-typing" />
                        </div>
                    )}
                    <div ref={chatEndRef} />
                </div>

                {/* Input Area */}
                <div style={{ padding: '1.25rem', background: '#fff', borderTop: '1px solid var(--gray-100)' }}>
                    <form onSubmit={handleSend} style={{ position: 'relative', maxWidth: '800px', margin: '0 auto' }}>
                        <input 
                            className="form-input" 
                            value={input} 
                            onChange={e => setInput(e.target.value)}
                            placeholder="Tanyakan sesuatu tentang data Ngada (misal: 'Berapa jumlah penduduk Bajawa?' atau 'cari data kemiskinan')..."
                            style={{ 
                                padding: '0.85rem 4rem 0.85rem 1.5rem', fontSize: '0.95rem', borderRadius: '30px', 
                                background: 'var(--gray-50)', border: '1px solid var(--gray-200)',
                                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                            }}
                        />
                        <button 
                            type="submit" 
                            disabled={loading || !input.trim()}
                            style={{ 
                                position: 'absolute', right: '6px', top: '6px', 
                                background: 'var(--primary-600)', border: 'none', 
                                width: '40px', height: '40px', borderRadius: '50%', 
                                color: '#fff', display: 'flex', alignItems: 'center', 
                                justifyContent: 'center', cursor: 'pointer',
                                transition: 'all 0.2s',
                                opacity: !input.trim() ? 0.5 : 1
                            }}
                        >
                            <FiSend size={18} />
                        </button>
                    </form>
                    <div style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.7rem', color: 'var(--gray-400)' }}>
                        NgadhuAI dapat melakukan kesalahan. Pastikan untuk memverifikasi data penting dengan OPD terkait.
                    </div>
                </div>
            </div>

            <style>{`
                .dot-typing {
                    position: relative;
                    left: -9999px;
                    width: 6px;
                    height: 6px;
                    border-radius: 5px;
                    background-color: var(--primary-500);
                    color: var(--primary-500);
                    box-shadow: 9984px 0 0 0 var(--primary-500), 9999px 0 0 0 var(--primary-500), 10014px 0 0 0 var(--primary-500);
                    animation: dotTyping 1.5s infinite linear;
                    margin: 0 15px;
                }
                @keyframes dotTyping {
                    0% { box-shadow: 9984px 0 0 0 var(--primary-500), 9999px 0 0 0 var(--primary-500), 10014px 0 0 0 var(--primary-500); }
                    16.667% { box-shadow: 9984px -10px 0 0 var(--primary-500), 9999px 0 0 0 var(--primary-500), 10014px 0 0 0 var(--primary-500); }
                    33.333% { box-shadow: 9984px 0 0 0 var(--primary-500), 9999px 0 0 0 var(--primary-500), 10014px 0 0 0 var(--primary-500); }
                    50% { box-shadow: 9984px 0 0 0 var(--primary-500), 9999px -10px 0 0 var(--primary-500), 10014px 0 0 0 var(--primary-500); }
                    66.667% { box-shadow: 9984px 0 0 0 var(--primary-500), 9999px 0 0 0 var(--primary-500), 10014px 0 0 0 var(--primary-500); }
                    83.333% { box-shadow: 9984px 0 0 0 var(--primary-500), 9999px 0 0 0 var(--primary-500), 10014px -10px 0 0 var(--primary-500); }
                    100% { box-shadow: 9984px 0 0 0 var(--primary-500), 9999px 0 0 0 var(--primary-500), 10014px 0 0 0 var(--primary-500); }
                }
                .dataset-link-card:hover {
                    border-color: var(--primary-300) !important;
                    background: var(--primary-50) !important;
                    transform: translateY(-2px);
                }
            `}</style>
        </div>
    );
};

export default DashboardAI;
